'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useInterval, useRafState } from 'react-use';

import { Styles, css } from '@flipster/styled-system/css';
import { convertDate } from '@flipster/utils/datetime';

import { useMounted } from '../misc/lifecycle/mount';

const Units = ['yy', 'MM', 'dd', 'hh', 'mm', 'ss'] as const;
type UnitType = (typeof Units)[number];

const IntervalUnitMapper: { [key in UnitType]: number } = {
  yy: 1000 * 60 * 60 * 24 * 365, // 1년 (평년 기준)
  MM: 1000 * 60 * 60 * 24 * 30, // 월은 평균 30일로 가정
  dd: 1000 * 60 * 60 * 24, // 1일
  hh: 1000 * 60 * 60, // 1시간
  mm: 1000 * 60, // 1분
  ss: 1000, // 1초
} as const;

interface FormattedDatePart {
  type: 'number' | 'text';
  value: string;
  unit?: UnitType;
}

const parseAndFormatLeftDate = (from: Date, to: Date, format: string): FormattedDatePart[] => {
  let difference = to.getTime() - from.getTime();
  const unitValues: { [key in UnitType]: number } = {
    yy: 0,
    MM: 0,
    dd: 0,
    hh: 0,
    mm: 0,
    ss: 0,
  };

  // 각 단위별 값 계산
  for (const unit of Units) {
    let diffUnitDistance = 0;
    if (difference > 0 && format.includes(`\${${unit}}`)) {
      diffUnitDistance = Math.floor(difference / IntervalUnitMapper[unit]);
      difference %= IntervalUnitMapper[unit];
    }
    unitValues[unit] = diffUnitDistance;
  }

  // format 문자열을 파싱하여 숫자와 텍스트 부분으로 분리
  const parts: FormattedDatePart[] = [];
  let currentText = '';
  let i = 0;
  while (i < format.length) {
    if (format.slice(i, i + 2) === '${') {
      // 현재까지의 텍스트가 있다면 추가
      if (currentText) {
        parts.push({ type: 'text', value: currentText });
        currentText = '';
      }
      // 단위 찾기
      const endIndex = format.indexOf('}', i);
      if (endIndex !== -1) {
        const unitStr = format.slice(i + 2, endIndex) as UnitType;
        if (Units.includes(unitStr)) {
          parts.push({
            type: 'number',
            value: String(unitValues[unitStr]).padStart(2, '0'),
            unit: unitStr,
          });
        }
        i = endIndex + 1;
      } else {
        currentText += format[i];
        i++;
      }
    } else {
      currentText += format[i];
      i++;
    }
  }
  // 마지막 텍스트 추가
  if (currentText) {
    parts.push({ type: 'text', value: currentText });
  }
  return parts;
};

interface CountdownDateProps {
  value: Date | string;
  format?: string;
  overText?: string;
  onEnd?: () => void;
  css?: Styles;
}

/**
 * @param value Date | string
 * @param format string = (ex) '${MM}:${dd} ${hh}:${mm}:${ss}'
 * @param overText string = countdown 이 끝나서 0 이 되었을때 다른 텍스트를 보여줘야할때 사용
 * @param onEnd () => void = countdown 이 끝나는 시점에 호출할 콜백함수
 * @param css Styles = 전체 컨테이너에 적용될 스타일
 */
export const CountdownDate = ({
  value,
  format = '${hh}:${mm}:${ss}',
  overText = '',
  onEnd,
  css: cssProps,
}: CountdownDateProps) => {
  const onEndCalled = useRef(false); // onEnd가 한번만 호출될 것을 보장하기 위함
  const [fromDate, setFromDate] = useRafState<Date | null>(null);
  const isMounted = useMounted();

  const toDate = convertDate(value);
  const isAfterFromDate = fromDate && toDate && fromDate.getTime() > toDate.getTime();

  const setNowTime = useCallback(() => {
    if (!isAfterFromDate) {
      setFromDate(new Date());
      onEndCalled.current = false;
    } else if (!onEndCalled.current) {
      onEnd?.();
      onEndCalled.current = true;
    }
  }, [isAfterFromDate, onEnd, setFromDate]);

  useEffect(() => {
    setFromDate(new Date());
  }, [setFromDate]);

  useInterval(setNowTime, 1000);

  useEffect(() => {
    if (isAfterFromDate) return;

    document.addEventListener('visibilitychange', setNowTime);
    return () => {
      document.removeEventListener('visibilitychange', setNowTime);
    };
  }, [isAfterFromDate, setNowTime]);

  const className = css({ wordBreak: 'keep-all' }, cssProps);

  if (!isMounted || !fromDate || !toDate) return <p className={className} translate='no' />;
  if (isAfterFromDate && overText)
    return (
      <p className={className} translate='no'>
        {overText}
      </p>
    );

  const formattedParts = parseAndFormatLeftDate(fromDate, toDate, format);

  return (
    <p className={className} translate='no'>
      {formattedParts.map((part, index) => {
        if (part.type === 'number') return <span key={`${part.type}-${index}`}>{part.value}</span>;
        return part.value;
      })}
    </p>
  );
};
